import { productImages, categoryHeroes } from "./images";

// Availability helper. Pass an array of month indices (0=Jan).
const A = (months) =>
  ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"]
    .map((m, i) => [m, months.includes(i)])
    .reduce((acc, [m, v]) => ({ ...acc, [m]: v }), {});

const ALL = A([0,1,2,3,4,5,6,7,8,9,10,11]);

export const categories = [
  {
    key: "vegetables",
    label: "Fresh Vegetables",
    short: "Fresh",
    accent: "from-amber-300 to-amber-600",
    ring: "ring-amber-300/40",
    hero: categoryHeroes.vegetables,
    emoji: "🌿",
    description:
      "Sourced directly from partner farms across Maharashtra and neighbouring states, every consignment is graded, packed and pre-cooled to international export standards.",
    items: [
      {
        slug: "onion",
        name: "Onion",
        emoji: "🧅",
        img: productImages.onion,
        note: "Red, Pink & White — sizes 35+ (35–80 mm)",
        season: "Year-round",
        tagline: "India's most exported vegetable, sourced from high-yield regions of Maharashtra & Gujarat.",
        description:
          "Our red, pink and white onions are sourced from cluster farms in Nashik, Lasalgaon and Bhavnagar — three of India's most prolific onion belts. Each lot is hand-graded, cured for shelf life, and packed in mesh sacks or jute bags for long-haul reefer transit.",
        varieties: [
          { name: "Red Onion (Nashik)", note: "Deep maroon skin, sharp pungency, 50–70 mm" },
          { name: "Light Red / Pink", note: "Mild flavour, popular in GCC & SE Asia, 40–60 mm" },
          { name: "White Onion", note: "Sweet, low-pungent, premium retail packs" },
        ],
        availability: ALL,
        specs: [
          { label: "Sizes", value: "35–80 mm (35+)" },
          { label: "Packing", value: "5/10/20/45/50 kg mesh bags" },
          { label: "Container", value: "26 MT / 40' reefer" },
          { label: "Origin", value: "Maharashtra, Gujarat" },
        ],
        highlights: [
          "Cured for 7–10 days post-harvest for extended shelf life",
          "Free from black mould, sprouting and double bulbs",
          "Phytosanitary, FSSAI and APEDA compliant",
        ],
      },
      {
        slug: "potato",
        name: "Potato",
        emoji: "🥔",
        img: "https://images.unsplash.com/photo-1518977676601-b53f82aba655?auto=format&fit=crop&w=900&q=80",
        note: "Table & processing grade — 50–100 mm",
        season: "Year-round",
        tagline: "Premium Indian potatoes — table-grade and processing-grade — for retail and food manufacturing.",
        description:
          "Sourced from Punjab, Uttar Pradesh and Madhya Pradesh, our potatoes are graded by size, washed and packed for export. Available in table-grade for retail and processing-grade (high dry-matter, low sugar) for chip and french-fry manufacturing.",
        varieties: [
          { name: "Kufri Pukhraj", note: "Oval, smooth skin, table-grade" },
          { name: "Kufri Jyoti", note: "All-purpose, popular for retail" },
          { name: "Santana / FC-3", note: "Processing-grade, high dry-matter" },
        ],
        availability: ALL,
        specs: [
          { label: "Sizes", value: "50–100 mm" },
          { label: "Packing", value: "10/25/50 kg jute, mesh" },
          { label: "Container", value: "26 MT / 40' reefer" },
          { label: "Origin", value: "Punjab, UP, MP" },
        ],
        highlights: [
          "Hand-sorted, free from greening and blight",
          "Cold-stored at 2–4 °C for shelf-life integrity",
          "Available in table & processing grades",
        ],
      },
      {
        slug: "green-chilli",
        name: "Green Chilli",
        emoji: "🌶️",
        img: productImages.greenChilli,
        note: "Medium & hot varieties — vacuum packs available",
        season: "Year-round",
        tagline: "Vibrant, fiery green chillies popular across the Middle East, Europe and Southeast Asia.",
        description:
          "Hand-picked from farms across Andhra Pradesh and Karnataka, our green chillies are sorted by length, colour and pungency. Vacuum and modified-atmosphere packs available for buyers in Europe and the GCC who require extended shelf life.",
        varieties: [
          { name: "G4", note: "Medium-hot, glossy skin, popular for retail" },
          { name: "Bydagi (Green)", note: "Mild heat, deep colour" },
          { name: "Pusa Jwala", note: "Hot, slender, foodservice grade" },
        ],
        availability: ALL,
        specs: [
          { label: "Length", value: "8–14 cm" },
          { label: "Packing", value: "5/10 kg cartons, vac packs" },
          { label: "Container", value: "+8 °C reefer" },
          { label: "Origin", value: "AP, Karnataka, Maharashtra" },
        ],
        highlights: [
          "Pre-cooled within 4 hours of harvest",
          "MA / vacuum packs extend shelf life to 21 days",
          "Pesticide residue tested for EU MRLs",
        ],
      },
      {
        slug: "okra",
        name: "Okra",
        emoji: "🫛",
        img: productImages.okra,
        note: "Tender lady finger, 8–12 cm, export grade",
        season: "Mar – Nov",
        tagline: "Tender, vibrant green okra — a staple across Middle Eastern and South Asian markets.",
        description:
          "Hand-harvested at the tender stage from partner farms across Maharashtra and Gujarat. Pre-cooled, graded by length, and packed in ventilated cartons for air or sea reefer freight.",
        varieties: [
          { name: "Singham / OH-152", note: "Dark green, tender, 8–12 cm" },
          { name: "Salkeerthi", note: "Long-fingered, vibrant colour" },
        ],
        availability: A([2,3,4,5,6,7,8,9,10]),
        specs: [
          { label: "Length", value: "8–12 cm" },
          { label: "Packing", value: "3/5 kg ventilated cartons" },
          { label: "Container", value: "+10 °C reefer / air" },
          { label: "Origin", value: "Maharashtra, Gujarat" },
        ],
        highlights: [
          "Hand-harvested daily at tender stage",
          "Pre-cooled within 6 hours of pick",
          "Popular in GCC, EU and SE Asian retail",
        ],
      },
      {
        slug: "sweet-corn",
        name: "Sweet Corn",
        emoji: "🌽",
        img: productImages.sweetCorn,
        note: "Fresh on-cob & IQF kernels",
        season: "Jun – Oct",
        tagline: "Fresh sweet corn cobs and IQF kernels for retail and foodservice.",
        description:
          "Sweet, juicy corn from cluster farms across Maharashtra and Karnataka. Available fresh on-the-cob with husk, or as IQF kernels for the foodservice and ready-meal channels.",
        varieties: [
          { name: "Sugar 75", note: "Premium sweetness, retail-grade" },
          { name: "Misti / Win Orange", note: "High brix, processing-grade" },
        ],
        availability: A([5,6,7,8,9]),
        specs: [
          { label: "Cob length", value: "16–22 cm" },
          { label: "Packing", value: "10 kg ventilated cartons / IQF bags" },
          { label: "Container", value: "+4 °C reefer / -18 °C IQF" },
          { label: "Origin", value: "Maharashtra, Karnataka" },
        ],
        highlights: [
          "Brix tested at harvest for sweetness consistency",
          "IQF kernels packed in 1 / 5 / 10 kg formats",
          "Husk-on packing protects freshness during transit",
        ],
      },
      {
        slug: "ginger",
        name: "Ginger",
        emoji: "🫚",
        img: productImages.ginger,
        note: "Fresh rhizomes, 200 g+ pieces",
        season: "Year-round",
        tagline: "Aromatic, fibre-rich ginger from the foothills of the Western Ghats.",
        description:
          "Sourced from Karnataka and the North-East, our fresh ginger is washed, graded by piece weight and packed in jute or carton boxes for export. Available with low-fibre and high-oil profiles for buyers in Europe and the GCC.",
        varieties: [
          { name: "Maran", note: "High-oil, premium" },
          { name: "Rio-de-Janeiro", note: "Low fibre, large rhizomes" },
        ],
        availability: ALL,
        specs: [
          { label: "Piece size", value: "150–400 g" },
          { label: "Packing", value: "10 / 20 kg jute, cartons" },
          { label: "Container", value: "+13 °C reefer" },
          { label: "Origin", value: "Karnataka, NE India" },
        ],
        highlights: [
          "Washed, dried and hand-sorted",
          "Available in Class A and Class B grades",
          "Free from mould, cuts and broken fingers",
        ],
      },
    ],
  },

  {
    key: "fruits",
    label: "Fresh Fruits",
    short: "Tropical",
    accent: "from-indigo-300 to-indigo-600",
    ring: "ring-indigo-300/40",
    hero: categoryHeroes.fruits,
    emoji: "🥭",
    description:
      "Tropical fruits from certified orchards, pack-house graded and shipped in temperature-controlled reefer containers for full traceability.",
    items: [
      {
        slug: "pomegranate",
        name: "Pomegranate",
        emoji: "🍎",
        img: productImages.pomegranate,
        note: "Bhagwa & Ganesh — deep ruby arils",
        season: "Year-round",
        tagline: "India is among the world's top pomegranate exporters — our Bhagwa and Ganesh varieties lead Gulf and EU markets.",
        description:
          "Pomegranate is a fruit formed by a combination of unit-like fruitlets — a round berry with a thick rind protecting juicy, jewel-like arils. Colour ranges from deep ruby red to yellow-green, with a bittersweet flavour. We supply fresh pomegranate on a year-round basis through our network of associate farmers across Maharashtra and Gujarat.",
        varieties: [
          { name: "Bhagwa", note: "Deep red arils, sweet, premium export grade" },
          { name: "Ganesh", note: "Pink-red arils, slightly tart, retail favourite" },
          { name: "Arakta", note: "Hardy, large fruit, deep colour" },
        ],
        availability: A([0,1,2,3,6,7,8,9,10,11]),
        specs: [
          { label: "Sizes", value: "200–500 g (Class A/B)" },
          { label: "Packing", value: "3.5 / 4.5 kg cartons with foam mesh" },
          { label: "Container", value: "+5 °C reefer, 90% RH" },
          { label: "Origin", value: "Maharashtra, Gujarat" },
        ],
        highlights: [
          "Pre-cooled to +5 °C within 6 hours of pick",
          "Foam-mesh wrapped to prevent rind damage",
          "EU MRL compliant, BAP residue tested",
        ],
      },
      {
        slug: "banana",
        name: "Banana",
        emoji: "🍌",
        img: productImages.banana,
        note: "Cavendish & Robusta — 13 / 13.5 / 18 kg cartons",
        season: "Year-round",
        tagline: "Premium raw and ripened bananas from Maharashtra — supplied in custom grades, sizes and pack formats.",
        description:
          "We supply premium-quality raw and ripe bananas from Maharashtra, India — one of the world's largest banana-producing regions. Cavendish and Robusta varieties are the workhorses of global trade, ideal for high-volume markets across Asia and the Middle East. Available in different grades, sizes and packing formats based on buyer requirements.",
        varieties: [
          { name: "Grand Naine / G9 (Cavendish)", note: "Long fingers, uniform shape, export favourite" },
          { name: "Robusta", note: "Slightly shorter, sweet, preferred in GCC" },
        ],
        availability: ALL,
        specs: [
          { label: "Packing", value: "13 / 13.5 / 18 kg telescopic cartons" },
          { label: "Container", value: "13 °C reefer, ethylene-controlled" },
          { label: "Origin", value: "Maharashtra" },
        ],
        highlights: [
          "Harvested at calibre 39–46 mm for export windows",
          "Latex-cleaned, fungicide-treated, foam-padded",
          "Custom hand-counts available for retail SKUs",
        ],
      },
      {
        slug: "mango",
        name: "Mango",
        emoji: "🥭",
        img: productImages.mango,
        note: "Alphonso, Kesar, Banganapalli — HWT treated",
        season: "Apr – Jul",
        tagline: "Indian mangoes are known for their unique flavour, sweetness and richness — and India is the world's largest producer.",
        description:
          "Indian varieties of mango are renowned globally for their unique flavour, sweetness and richness. We export many varieties, but specialise in Alphonso, Kesar, Badami and Rajapuri. Each shipment is hot-water-treated (HWT) for fruit-fly disinfestation where required, and packed in standard or customised retail formats.",
        varieties: [
          { name: "Alphonso", note: "Saffron pulp, intensely sweet — the king of mangoes" },
          { name: "Kesar", note: "Saffron-coloured, aromatic, late-season" },
          { name: "Banganapalli", note: "Large, oval, mild sweetness" },
          { name: "Badami / Rajapuri", note: "Regional favourites, bulk grades" },
        ],
        availability: A([3,4,5,6]),
        specs: [
          { label: "Fruit weight", value: "200–500 g" },
          { label: "Packing", value: "2 / 4 / 5 kg cartons" },
          { label: "Container", value: "+12 °C reefer / air freight" },
          { label: "Origin", value: "Maharashtra, Gujarat, AP" },
        ],
        highlights: [
          "Hot-water treatment (HWT) for US/EU markets",
          "Vapour heat treatment available for Japan/Korea",
          "Tray-packed with paper cushion for retail presentation",
        ],
      },
      {
        slug: "table-grapes",
        name: "Table Grapes",
        emoji: "🍇",
        img: productImages.tableGrapes,
        note: "Thompson, Sharad, Jumbo, Flame & Sonaka Seedless",
        season: "Nov – May",
        tagline: "Nashik — India's grape capital — produces some of the world's sweetest, most colourful table grapes.",
        description:
          "Nashik district is the largest grape-producing area in India. Its perfect climatic conditions promote grapes that are high in sweetness, nutritional value, freshness, colour and size. We have been active in the export of fresh grapes since 1993 and continue to grow with the changing global demand.",
        varieties: [
          { name: "Thompson / White Seedless", note: "Sweet, crisp, light green — global favourite" },
          { name: "Sharad / Black Seedless", note: "Deep purple, intensely sweet" },
          { name: "Jumbo Seedless", note: "Large berries, crisp texture" },
          { name: "Flame Seedless", note: "Bright red, sweet-tart" },
          { name: "Sonaka Seedless", note: "Long oval berry, mid-sweet" },
        ],
        // From PDF: Thompson Jan-May+Nov-Dec; others Jan-Mar+Nov-Dec.
        // Showing union here (any variety available)
        availability: A([0,1,2,3,4,10,11]),
        availabilityByVariety: [
          { name: "Thompson / White Seedless", months: A([0,1,2,3,4,10,11]) },
          { name: "Sharad / Black Seedless",   months: A([0,1,2,10,11]) },
          { name: "Jumbo Seedless",            months: A([0,1,2,10,11]) },
          { name: "Flame Seedless",            months: A([0,1,2,10,11]) },
          { name: "Sonaka Seedless",           months: A([0,1,2,10,11]) },
        ],
        specs: [
          { label: "Berry size", value: "16–24 mm" },
          { label: "Packing", value: "4.5 / 5 / 8 kg punnet or open cartons" },
          { label: "Container", value: "0–2 °C reefer with SO₂ pads" },
          { label: "Origin", value: "Nashik, Sangli (Maharashtra)" },
        ],
        highlights: [
          "Harvested at 16–18° brix for export sweetness",
          "SO₂ generator pads for 30–45 day transit life",
          "EU MRL compliant, GlobalG.A.P. farms",
        ],
      },
      {
        slug: "coconut",
        name: "Coconut",
        emoji: "🥥",
        img: productImages.coconut,
        note: "Mature semi-husked & tender drinking coconut",
        season: "Year-round",
        tagline: "Mature semi-husked coconut and tender drinking coconut from the southern coast.",
        description:
          "Sourced from coastal Karnataka, Tamil Nadu and Kerala, our mature coconuts are semi-husked and trimmed for export presentation. Tender drinking coconuts are wax-coated to preserve freshness and arrive with sweet, hydrating water intact.",
        varieties: [
          { name: "Semi-husked Mature", note: "Hard kernel, retail & processing" },
          { name: "Tender Drinking", note: "Wax-coated, 1.2–1.6 kg, sweet water" },
        ],
        availability: ALL,
        specs: [
          { label: "Weight", value: "0.5–1.6 kg" },
          { label: "Packing", value: "PP mesh, jute, or trimmed cartons" },
          { label: "Container", value: "Ambient / +4 °C reefer (tender)" },
          { label: "Origin", value: "Karnataka, TN, Kerala" },
        ],
        highlights: [
          "Trimmed and waxed for retail shelf appeal",
          "Tested for shell cracks and water leakage",
          "FCL volumes year-round",
        ],
      },
    ],
  },

  {
    key: "spices",
    label: "Spices",
    short: "Aromatic",
    accent: "from-amber-300 to-orange-600",
    ring: "ring-amber-300/40",
    hero: categoryHeroes.spices,
    emoji: "🌶️",
    description:
      "Sun-dried, steam-sterilized and traceable from farm to facility — the spices that define Indian cuisine, packed to international food-safety standards.",
    items: [
      {
        slug: "turmeric",
        name: "Turmeric",
        emoji: "🟡",
        img: productImages.turmeric,
        note: "Polished fingers & powder — 3–5% curcumin",
        season: "Year-round",
        tagline: "India produces ~80% of the world's turmeric — we supply Salem, Erode and Sangli origins.",
        description:
          "Available as polished fingers, bulbs and powder, our turmeric is sourced from Salem and Erode in Tamil Nadu and Sangli in Maharashtra. Curcumin content of 3–5% is standard, with high-curcumin (>5%) origins available for nutraceutical buyers.",
        varieties: [
          { name: "Salem Polished Finger", note: "Premium, deep yellow, 3–4% curcumin" },
          { name: "Erode Bulb", note: "Round, high oil, processing grade" },
          { name: "Lakadong / Nizamabad", note: "High curcumin (>5%), nutraceutical grade" },
        ],
        availability: ALL,
        specs: [
          { label: "Curcumin", value: "3–5% (standard) / >5% (premium)" },
          { label: "Packing", value: "25 / 50 kg PP woven bags" },
          { label: "Container", value: "Ambient" },
          { label: "Origin", value: "TN, Maharashtra, Telangana" },
        ],
        highlights: [
          "Steam-sterilized to ASTA / ESA standards",
          "Polished, machine-cleaned, hand-sorted",
          "Aflatoxin & pesticide MRL tested",
        ],
      },
      {
        slug: "cumin",
        name: "Cumin",
        emoji: "🌾",
        img: productImages.cumin,
        note: "Singapore / Europe quality — 99.5% purity",
        season: "Year-round",
        tagline: "Cumin from the arid plains of Gujarat and Rajasthan — the world's leading export origin.",
        description:
          "Our cumin is sourced from Unjha (Gujarat) — the world's largest cumin trading hub — and from Rajasthan. Available in Singapore quality (99% purity) and Europe quality (99.5%+) with steam-sterilization for food-safety-conscious markets.",
        varieties: [
          { name: "Singapore Quality", note: "99% purity, foodservice grade" },
          { name: "Europe Quality", note: "99.5%+ purity, retail grade" },
        ],
        availability: ALL,
        specs: [
          { label: "Purity", value: "99% / 99.5%+" },
          { label: "Packing", value: "25 / 50 kg PP woven, jute" },
          { label: "Container", value: "Ambient" },
          { label: "Origin", value: "Gujarat, Rajasthan" },
        ],
        highlights: [
          "Steam-sterilized to ESA standards",
          "Magnetic & gravity cleaned",
          "Volatile oil 2.5%+ guaranteed",
        ],
      },
      {
        slug: "coriander",
        name: "Coriander",
        emoji: "🌿",
        img: productImages.coriander,
        note: "Eagle, Single & Double parrot quality",
        season: "Year-round",
        tagline: "Whole and split coriander seed from Rajasthan and Madhya Pradesh.",
        description:
          "We supply Eagle, Single Parrot and Double Parrot grade coriander seed — sourced from Kota (Rajasthan) and Guna (MP). Cleaned, sortexed and packed to international standards.",
        varieties: [
          { name: "Eagle Quality", note: "Premium, light green-yellow, oil rich" },
          { name: "Single Parrot", note: "Standard retail grade" },
          { name: "Double Parrot", note: "Bulk, foodservice" },
        ],
        availability: ALL,
        specs: [
          { label: "Purity", value: "98–99.5%" },
          { label: "Packing", value: "25 / 50 kg PP woven" },
          { label: "Container", value: "Ambient" },
          { label: "Origin", value: "Rajasthan, MP" },
        ],
        highlights: [
          "Sortex-cleaned to remove discolour seeds",
          "Steam-sterilization on request",
          "Volatile oil 0.3%+ guaranteed",
        ],
      },
      {
        slug: "black-pepper",
        name: "Black Pepper",
        emoji: "⚫",
        img: productImages.blackPepper,
        note: "Malabar Garbled & Tellicherry, 550+ GL",
        season: "Year-round",
        tagline: "Malabar Garbled (MG1) and Tellicherry (TGEB) from the Western Ghats.",
        description:
          "Black pepper from the Western Ghats of Kerala and Karnataka — graded by berry size and density. Tellicherry Garbled Extra Bold (TGEB) is the premium grade, with 4.75 mm+ berries and 570+ GL density.",
        varieties: [
          { name: "Malabar Garbled MG1", note: "550 GL min, 4.25 mm+, retail grade" },
          { name: "Tellicherry TGEB", note: "570 GL min, 4.75 mm+, premium" },
        ],
        availability: ALL,
        specs: [
          { label: "Density", value: "550 / 570 GL" },
          { label: "Packing", value: "25 / 50 kg PP woven, jute" },
          { label: "Container", value: "Ambient" },
          { label: "Origin", value: "Kerala, Karnataka" },
        ],
        highlights: [
          "Steam-sterilized for EU markets",
          "Cleaned of stalks, light berries and dust",
          "Piperine 4%+ standard",
        ],
      },
      {
        slug: "cardamom",
        name: "Green Cardamom",
        emoji: "🟢",
        img: productImages.cardamom,
        note: "Mini-bold to +8 mm — full size & colour range",
        season: "Oct – Feb",
        tagline: "Alleppey green cardamom — graded across the full size and colour spectrum, intensely aromatic.",
        description:
          "Sourced from Idukki and Wayanad, our Alleppey green cardamom is sun-dried and hand-graded by pod size and colour. Available in the full grade range from mini-bold (5.5–6.5 mm) through +8 mm extra-bold, plus rejection grade for processing buyers.",
        varieties: [
          { name: "5.5–6.5 mm Mini Bold", note: "Compact pods, balanced aroma — entry retail grade" },
          { name: "6–7 mm Colour", note: "Vibrant green, classic retail size" },
          { name: "7–7.5 mm Colour", note: "Deep green, popular GCC & SE Asia spec" },
          { name: "7–8 mm Colour", note: "Bold pods, premium retail standard" },
          { name: "7–8.5 mm Colour", note: "Extended bold range, high oil yield" },
          { name: "7.5–8 mm Colour", note: "Tight grade, consistent uniform pack" },
          { name: "+8 mm Colour", note: "Extra-bold top tier, gift / premium foodservice" },
          { name: "7–8 mm Rejection", note: "Processing-grade — for oil extraction & ground spice" },
        ],
        availability: A([9,10,11,0,1]),
        specs: [
          { label: "Pod size", value: "5.5 – 8.5 mm+ (graded)" },
          { label: "Packing", value: "5 / 10 kg cartons, vacuum bags" },
          { label: "Container", value: "Ambient / +5 °C for premium" },
          { label: "Origin", value: "Kerala (Idukki), Karnataka" },
        ],
        highlights: [
          "Hand-picked, gradient sun-dried",
          "Vacuum-pack option preserves volatile oils",
          "Cineole 28%+ standard",
          "Tax & transport at actual — offer subject to daily availability",
        ],
      },
      {
        slug: "cinnamon",
        name: "Cinnamon",
        emoji: "🌰",
        img: productImages.cinnamon,
        note: "Ceylon quills & Cassia cuts / splits",
        season: "Year-round",
        tagline: "Ceylon true-cinnamon quills and Cassia cuts for retail and processing.",
        description:
          "Sourced from Ceylon (Sri Lanka) for true cinnamon, and Indian cassia for the bulk processing trade. Available as quills, cuts, splits and powder.",
        varieties: [
          { name: "Ceylon Alba / C5", note: "True cinnamon, premium quills" },
          { name: "Indian Cassia (Tej)", note: "Stronger flavour, bulk" },
        ],
        availability: ALL,
        specs: [
          { label: "Form", value: "Quills, cuts, splits, powder" },
          { label: "Packing", value: "10 / 25 kg cartons" },
          { label: "Container", value: "Ambient" },
          { label: "Origin", value: "Sri Lanka, NE India" },
        ],
        highlights: [
          "Coumarin tested for EU compliance",
          "Volatile oil 1.5%+ standard",
          "Hand-rolled Ceylon quills available",
        ],
      },
    ],
  },

  {
    key: "processed",
    label: "Processed",
    short: "Value-Added",
    accent: "from-slate-300 to-slate-500",
    ring: "ring-slate-300/40",
    hero: categoryHeroes.processed,
    emoji: "🍇",
    description:
      "Value-added products that extend shelf life, reduce logistics cost and open new product opportunities for food manufacturers, retailers and foodservice operators.",
    items: [
      {
        slug: "dehydrated-vegetables",
        name: "Dehydrated Vegetables",
        emoji: "🧅",
        img: productImages.dehydratedVegetables,
        note: "Onion flakes, garlic granules, tomato",
        season: "Year-round",
        tagline: "Onion flakes, garlic granules, dried tomato — shelf-stable building blocks for spice blends and ready meals.",
        description:
          "Our dehydrated vegetable range is a workhorse for spice-blend manufacturers, instant-food makers and ready-to-cook brands. Air-dried at controlled temperature to preserve colour, flavour and rehydration ratio.",
        varieties: [
          { name: "Onion Flakes / Granules / Powder", note: "White and pink, 4–8% moisture" },
          { name: "Garlic Granules / Powder", note: "Allicin-preserved, mesh sizes 8–80" },
          { name: "Dried Tomato", note: "Powder, flakes, sun-dried halves" },
        ],
        availability: ALL,
        specs: [
          { label: "Moisture", value: "4–8% max" },
          { label: "Packing", value: "10 / 20 / 25 kg cartons, foil-lined" },
          { label: "Container", value: "Ambient, low humidity" },
          { label: "Origin", value: "Maharashtra, Gujarat" },
        ],
        highlights: [
          "Steam-sterilized, microbial controlled",
          "Custom mesh / particle size available",
          "FSSC 22000 / HACCP packing facilities",
        ],
      },
      {
        slug: "fruit-pulps",
        name: "Fruit Pulps & Concentrates",
        emoji: "🥭",
        img: "https://images.unsplash.com/photo-1546548970-71785318a17b?auto=format&fit=crop&w=900&q=80",
        note: "Mango Alphonso, Totapuri, pomegranate",
        season: "Year-round (frozen)",
        tagline: "Pure, additive-free fruit pulps from mango, pomegranate and tropical fruits.",
        description:
          "Aseptic and frozen fruit pulps for the beverage, dairy and confectionery industries. Mango pulp is our flagship — Alphonso for premium and Totapuri for bulk applications. Pomegranate concentrate available for juice blenders.",
        varieties: [
          { name: "Alphonso Mango Pulp", note: "Premium, 15° brix, sweet aromatic" },
          { name: "Totapuri Mango Pulp", note: "16° brix, tart, processing grade" },
          { name: "Pomegranate Concentrate", note: "65° brix, deep red" },
        ],
        availability: ALL,
        specs: [
          { label: "Brix", value: "14–65°" },
          { label: "Packing", value: "215 kg aseptic drums, 1 kg retail" },
          { label: "Container", value: "Ambient (aseptic) / -18 °C (frozen)" },
          { label: "Origin", value: "Maharashtra, AP, Karnataka" },
        ],
        highlights: [
          "Aseptic processed for 18-month shelf life",
          "No added sugar, colour or preservatives",
          "FSSC 22000 / Halal / Kosher available",
        ],
      },
      {
        slug: "raisins",
        name: "Raisins",
        emoji: "🍇",
        img: productImages.raisins,
        note: "Green, golden & black — Nashik origin",
        season: "Year-round",
        tagline: "Indian raisins from Sangli and Nashik — green, golden and black grades.",
        description:
          "Our raisins are sun-dried from Thompson seedless grapes in Sangli and Nashik. Available in long-green, golden-yellow and black varieties — sortexed, metal-detected and packed for retail or industrial use.",
        varieties: [
          { name: "Long Green", note: "Premium, light green, retail" },
          { name: "Golden Yellow", note: "Sulphur-treated, sweet" },
          { name: "Black Raisins", note: "Naturally dried, antioxidant-rich" },
        ],
        availability: ALL,
        specs: [
          { label: "Moisture", value: "13–15%" },
          { label: "Packing", value: "12.5 kg cartons, retail pouches" },
          { label: "Container", value: "Ambient" },
          { label: "Origin", value: "Sangli, Nashik" },
        ],
        highlights: [
          "Sortexed and metal-detected",
          "Sulphur-free options available",
          "Custom retail pouching available",
        ],
      },
      {
        slug: "iqf-corn",
        name: "IQF Corn Cob & Kernels",
        emoji: "🌽",
        img: productImages.cornCob,
        note: "Frozen cobs & retail vacuum packs",
        season: "Year-round",
        tagline: "IQF sweet corn — cobs, half-cobs and kernels for retail and foodservice.",
        description:
          "Individually-Quick-Frozen sweet corn locks in peak-harvest sweetness. Available as full cobs, half cobs (cut for retail packs) and loose kernels — packed in 1, 5 or 10 kg formats.",
        varieties: [
          { name: "IQF Cobs", note: "16–22 cm, full or half-cut" },
          { name: "IQF Kernels", note: "Free-flowing, golden" },
        ],
        availability: ALL,
        specs: [
          { label: "Brix", value: "13°+" },
          { label: "Packing", value: "1 / 5 / 10 kg poly bags" },
          { label: "Container", value: "-18 °C reefer" },
          { label: "Origin", value: "Maharashtra, Karnataka" },
        ],
        highlights: [
          "Frozen within 4 hours of harvest",
          "Brix and visual defect tested",
          "BRC / FSSC 22000 packing facilities",
        ],
      },
    ],
  },
];

export const findCategory = (key) => categories.find((c) => c.key === key);

export const findProduct = (categoryKey, slug) => {
  const cat = findCategory(categoryKey);
  if (!cat) return null;
  const item = cat.items.find((i) => i.slug === slug);
  if (!item) return null;
  return { category: cat, item };
};

export const allProducts = categories.flatMap((c) =>
  c.items.map((i) => ({ ...i, category: c }))
);
